import { useEffect, useMemo, useState, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { fmtCur, fmtNum } from "../lib/format";
import { Receipt, Users, Package, CurrencyInr, MagnifyingGlass, ArrowClockwise, DownloadSimple, CaretRight } from "@phosphor-icons/react";
import SearchSelect from "../components/SearchSelect";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (m) => {
  const [y, mo] = (m || "").split("-");
  return mo ? `${MONTHS[+mo - 1]} '${y.slice(2)}` : m;
};
// Display dates as DD-MMM-YY (e.g. 01-Apr-26) from a YYYY-MM-DD string.
const fmtDate = (s) => {
  const [y, mo, d] = (s || "").slice(0, 10).split("-");
  return (y && mo && d) ? `${d}-${MONTHS[+mo - 1]}-${y.slice(2)}` : (s || "");
};
const SYM = { INR: "\u20B9", USD: "$", GBP: "\u00A3" };
// Compact money for summaries/totals: INR in Crores (e.g. ₹221.09 Cr), foreign in Millions ($24.95 Mn).
const compact = (v, ccy = "INR") => {
  const n = +v || 0;
  const s = SYM[ccy] || "";
  if (ccy === "INR") return `${s}${(n / 1e7).toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
  return `${s}${(n / 1e6).toLocaleString("en-US", { maximumFractionDigits: 2 })} Mn`;
};
const ccyChips = (obj) => Object.entries(obj || {}).filter(([, v]) => v)
  .map(([c, v]) => compact(v, c)).join("  \u00B7  ") || "\u2014";

// Pick the display currency for a row: if it trades in a SINGLE currency, show that NATIVE value
// (so USD customers/items show $ values); if it's mixed, fall back to the combined INR-equivalent.
const _EPS = 0.005;
const pickDisp = (byCcy, inrFallback) => {
  const ks = Object.keys(byCcy || {}).filter((k) => Math.abs(byCcy[k] || 0) > _EPS);
  if (ks.length === 1) return { ccy: ks[0], val: byCcy[ks[0]], mixed: false };
  return { ccy: "INR", val: inrFallback || 0, mixed: ks.length > 1 };
};
const mergeCcy = (a, b) => {
  const out = { ...(a || {}) };
  Object.keys(b || {}).forEach((k) => { out[k] = (out[k] || 0) + (b[k] || 0); });
  return out;
};
const INR_EQ = <span style={{ fontSize: 9, fontWeight: 600, color: "var(--omh-text-muted)", marginLeft: 3 }}>INR eq.</span>;

const LBL = { fontSize: 10.5, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3, display: "block" };
const INP = { width: "100%", height: 34, boxSizing: "border-box", padding: "6px 10px", border: "1px solid var(--omh-border)", borderRadius: 8, fontSize: 12.5, background: "#fff", color: "var(--omh-text)" };
const TH = { textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "2px solid var(--omh-border)", whiteSpace: "nowrap" };
const TD = { padding: "9px 12px", fontSize: 13, borderBottom: "1px solid var(--omh-border)", color: "var(--omh-text)" };
const MONO = { fontFamily: "Inter, sans-serif" };

function Stat({ label, value, sub, accent, icon: Icon, testid, onClick, active }) {
  return (
    <div data-testid={testid} onClick={onClick}
      style={{ background: active ? "#ECFDF5" : "#fff",
        borderStyle: "solid", borderTopWidth: "3px", borderRightWidth: "1px", borderBottomWidth: "1px", borderLeftWidth: "1px",
        borderTopColor: accent, borderRightColor: active ? accent : "var(--omh-border)",
        borderBottomColor: active ? accent : "var(--omh-border)", borderLeftColor: active ? accent : "var(--omh-border)",
        borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 180,
        cursor: onClick ? "pointer" : "default", transition: "background-color .15s, box-shadow .15s",
        boxShadow: active ? `0 0 0 3px ${accent}22` : "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, color: "var(--omh-text-muted)" }}>{label}</span>
        {Icon && <Icon size={19} weight="duotone" color={accent} />}
      </div>
      <div style={{ fontSize: 25, fontWeight: 800, marginTop: 8, ...MONO, color: "var(--omh-text)" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--omh-text-muted)", marginTop: 4 }}>{sub}</div>}
      {onClick && <div style={{ fontSize: 10.5, color: accent, fontWeight: 700, marginTop: 6 }}>{active ? "Showing breakdown · click to exit" : "Click to see customer breakdown"}</div>}
    </div>
  );
}

export default function SalesReport() {
  const [f, setF] = useState({ customer: "", date_from: "", date_to: "", month: "", item: "", item_number: "", currency: "" });
  const [view, setView] = useState("summary");
  const [projected, setProjected] = useState(false);   // By-Customer table shows projected open-order breakdown
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sugg, setSugg] = useState({ customers: [], items: [] });

  useEffect(() => {
    api.get("/ms/sales-report/suggest")
      .then((r) => setSugg({ customers: r.data.customers || [], items: r.data.items || [] }))
      .catch(() => {});
  }, []);

  const customerOpts = useMemo(
    () => (sugg.customers || []).map((c) => ({ value: c.name, label: c.name, sub: c.ms_pcode })),
    [sugg.customers]);
  const itemCodeOpts = useMemo(
    () => (sugg.items || []).map((it) => ({ value: it.code, label: it.code, sub: it.desc })),
    [sugg.items]);
  const itemDescOpts = useMemo(
    () => (sugg.items || []).filter((it) => it.desc)
      .map((it) => ({ value: it.desc, label: it.desc, sub: it.code })),
    [sugg.items]);

  const load = useCallback(async (overrideView, overrideF) => {
    setLoading(true); setErr("");
    try {
      const eff = overrideF || f;
      const params = {};
      Object.entries(eff).forEach(([k, v]) => { if (v) params[k] = v; });
      params.view = overrideView || view;
      const { data } = await api.get("/ms/sales-report", { params });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to load");
    } finally { setLoading(false); }
  }, [f, view]);

  useEffect(() => { load("summary"); /* initial FY load */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setVal = (k) => (val) => setF((p) => ({ ...p, [k]: val }));
  const reset = () => {
    const nextF = { customer: "", date_from: "", date_to: "", month: "", item: "", item_number: "", currency: "" };
    setF(nextF); load("summary", nextF);
  };

  const ccys = useMemo(() => Object.keys(data?.totals?.by_ccy || {}), [data]);
  // When the Projected Sales tile is active, the By-Customer table shows the open-order backlog split.
  const projSummary = useMemo(() => data?.projected_summary || [], [data]);
  const projCcys = useMemo(() => {
    const s = new Set();
    projSummary.forEach((r) => Object.keys(r.by_ccy || {}).forEach((c) => s.add(c)));
    return Array.from(s);
  }, [projSummary]);
  const rows = projected ? projSummary : (data?.summary || []);
  const activeCcys = projected ? projCcys : ccys;

  const exportCsv = () => {
    if (!data) return;
    let rows = [];
    if (projected) {
      rows.push(["Customer", "ms_pcode", ...projCcys, "Open Value (INR)", "Open Qty", "Open Lines"]);
      projSummary.forEach((r) => rows.push([r.customer_name, r.ms_pcode, ...projCcys.map((c) => r.by_ccy[c] || 0), r.net_value_inr, r.qty, r.line_count]));
    } else if (view === "item") {
      rows.push(["Item Code", "Description", "Shipped Qty", "Shipped Value", "To-Ship Qty", "To-Ship Value", "Order Book", "Currency", "Shipped Value (INR eq.)", "To-Ship Value (INR eq.)", "Order Book (INR eq.)"]);
      (data.item_summary || []).forEach((r) => {
        const sD = pickDisp(r.shipped_by_ccy, r.shipped_value_inr);
        const tD = pickDisp(r.to_ship_by_ccy, r.to_ship_value_inr);
        const bD = pickDisp(mergeCcy(r.shipped_by_ccy, r.to_ship_by_ccy), r.order_book_value_inr);
        rows.push([r.item_code, r.item_desc, r.shipped_qty, sD.val, r.to_ship_qty, tD.val, bD.val, bD.ccy, r.shipped_value_inr, r.to_ship_value_inr, r.order_book_value_inr]);
      });
    } else if (view === "detail") {
      rows.push(["Date", "GST Inv Num", "Export Inv Num", "Customer", "Item Code", "Item Description", "PO", "Qty", "Rate", "Currency", "Net Value", "Net Value (INR)"]);
      (data.detail || []).forEach((r) => rows.push([fmtDate(r.sale_date), r.invoice_no, r.export_invoice_no, r.customer_name, r.item_code, r.item_desc, r.pono, r.qty, r.rate, r.currency, r.net_value, r.net_value_inr]));
    } else {
      rows.push(["Customer", "ms_pcode", ...ccys, "Net Value (INR)", "Qty", "Invoices"]);
      (data.summary || []).forEach((r) => rows.push([r.customer_name, r.ms_pcode, ...ccys.map((c) => r.by_ccy[c] || 0), r.net_value_inr, r.qty, r.invoice_count]));
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sales-report-${projected ? "projected" : view}-${data.date_from}_${data.date_to}.csv`;
    a.click();
  };

  const switchView = (v) => { setView(v); load(v); };

  return (
    <Layout>
      <div data-testid="sales-report-page" style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--omh-text)", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
              <Receipt size={26} weight="duotone" color="#0369A1" /> Sales Report
            </h1>
            <p style={{ color: "var(--omh-text-muted)", fontSize: 13, margin: "6px 0 0" }}>
              Sales recognised on <b>GST invoice</b> creation · net of GST · {data?.fy_label ? <>{data.fy_label} ({data.date_from} → {data.date_to})</> : "current financial year"}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button data-testid="sales-report-export-btn" onClick={exportCsv} disabled={!data}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 8, border: "1px solid var(--omh-border)", background: "#fff", color: "var(--omh-text)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              <DownloadSimple size={17} /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: "#fff", border: "1px solid var(--omh-border)", borderRadius: 12, padding: "16px 18px", marginBottom: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, alignItems: "end" }}>
            <div>
              <label style={LBL}>Customer</label>
              <SearchSelect testid="sales-report-filter-customer" value={f.customer}
                onChange={setVal("customer")} options={customerOpts} placeholder="All customers" />
            </div>
            <div>
              <label style={LBL}>Month of sale</label>
              <input type="month" data-testid="sales-report-filter-month" style={INP} value={f.month} onChange={set("month")} />
            </div>
            <div>
              <label style={LBL}>From date</label>
              <input type="date" data-testid="sales-report-filter-from" style={INP} value={f.date_from} onChange={set("date_from")} disabled={!!f.month} />
            </div>
            <div>
              <label style={LBL}>To date</label>
              <input type="date" data-testid="sales-report-filter-to" style={INP} value={f.date_to} onChange={set("date_to")} disabled={!!f.month} />
            </div>
            <div>
              <label style={LBL}>Item (description)</label>
              <SearchSelect testid="sales-report-filter-item" value={f.item}
                onChange={setVal("item")} options={itemDescOpts} placeholder="e.g. Mount Assembly" />
            </div>
            <div>
              <label style={LBL}>Item number (code)</label>
              <SearchSelect testid="sales-report-filter-item-number" value={f.item_number}
                onChange={setVal("item_number")} options={itemCodeOpts} placeholder="e.g. TM-0044" />
            </div>
            <div>
              <label style={LBL}>Currency</label>
              <select data-testid="sales-report-filter-currency" style={INP} value={f.currency} onChange={set("currency")}>
                <option value="">All</option>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
              <button data-testid="sales-report-apply-btn" onClick={() => load()} disabled={loading}
                style={{ flex: 1, height: 34, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: 6, padding: "0 14px", borderRadius: 8, border: "none", background: "#0369A1", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                <MagnifyingGlass size={15} /> Apply
              </button>
              <button data-testid="sales-report-reset-btn" onClick={reset} title="Reset to current FY"
                style={{ height: 34, padding: "0 11px", borderRadius: 8, border: "1px solid var(--omh-border)", background: "#fff", color: "var(--omh-text-muted)", cursor: "pointer" }}>
                <ArrowClockwise size={15} />
              </button>
            </div>
          </div>
        </div>

        {err && <div data-testid="sales-report-error" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{err}</div>}

        {/* Stat tiles */}
        {data && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
            <Stat testid="sales-report-stat-total" label="Total sales (INR equiv)" accent="#0369A1" icon={CurrencyInr}
              value={compact(data.totals.net_value_inr, "INR")} sub={`FX: USD ₹${data.fx.usd_inr || "—"} · GBP ₹${data.fx.gbp_inr || "—"} (${data.fx.mode})`}
              onClick={() => setProjected(false)} active={!projected} />
            <Stat testid="sales-report-stat-ccy" label="By currency" accent="#7C3AED" icon={Receipt}
              value={<span style={{ fontSize: 15, lineHeight: 1.5 }}>{ccyChips(data.totals.by_ccy)}</span>} />
            <Stat testid="sales-report-stat-projected" label="Projected sales (open orders)" accent="#059669" icon={Package}
              value={compact(data.totals.projected_open_orders_inr || 0, "INR")}
              sub={`${fmtNum(data.totals.projected_open_orders_lines || 0)} open order lines · current backlog`}
              onClick={() => setProjected(true)} active={projected} />
            <Stat testid="sales-report-stat-pipeline" label="Pipeline total" accent="#D97706" icon={CurrencyInr}
              value={compact((data.totals.net_value_inr || 0) + (data.totals.projected_open_orders_inr || 0), "INR")}
              sub="recognised + projected" />
            <Stat testid="sales-report-stat-coverage" label="Invoiced coverage" accent="#0E7490" icon={Receipt}
              value={`${fmtNum(data.totals.invoice_count)} invoices`}
              sub={`${fmtNum(data.totals.customer_count)} customers · Qty ${fmtNum(data.totals.qty)}`} />
          </div>
        )}

        {/* By-month breakdown — hidden while the projected open-order breakdown is shown */}
        {data && !projected && data.by_month?.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid var(--omh-border)", borderRadius: 12, padding: "14px 18px", marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Sales by month (INR equiv)</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {data.by_month.map((m) => (
                <button key={m.month} data-testid={`sales-report-month-${m.month}`} onClick={() => { const nextF = { ...f, month: m.month }; setF(nextF); load(undefined, nextF); }}
                  style={{ border: "1px solid var(--omh-border)", borderRadius: 10, padding: "10px 14px", background: f.month === m.month ? "#EFF6FF" : "#fafafa", cursor: "pointer", textAlign: "left", minWidth: 120 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--omh-text-muted)" }}>{monthLabel(m.month)}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, ...MONO, color: "var(--omh-text)", marginTop: 3 }}>{compact(m.net_value_inr, "INR")}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* View toggle (By customer / Line detail) — only for invoiced sales; projected shows a single breakdown */}
        {projected ? (
          <div data-testid="sales-report-projected-banner" style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "#ECFDF5", border: "1px solid #A7F3D0", borderBottom: "none", borderRadius: "8px 8px 0 0", fontSize: 12.5, color: "#065F46", fontWeight: 600 }}>
            <Package size={15} weight="duotone" color="#059669" />
            Projected sales — open-order backlog by customer ({fmtNum(data?.totals?.projected_open_orders_lines || 0)} open lines).
            <button data-testid="sales-report-projected-exit" onClick={() => setProjected(false)}
              style={{ marginLeft: "auto", border: "none", background: "none", color: "#0369A1", fontWeight: 700, cursor: "pointer", fontSize: 12.5 }}>
              ← Back to invoiced sales
            </button>
          </div>
        ) : (
        <div style={{ display: "flex", gap: 2, marginBottom: 0, borderBottom: "1px solid var(--omh-border)" }}>
          {["summary", "item", "detail"].map((v) => (
            <button key={v} data-testid={`sales-report-view-${v}`} onClick={() => switchView(v)}
              style={{ padding: "9px 18px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, textTransform: "capitalize",
                color: view === v ? "#0369A1" : "var(--omh-text-muted)", borderBottom: view === v ? "2px solid #0369A1" : "2px solid transparent", marginBottom: -1 }}>
              {v === "summary" ? "By customer" : v === "item" ? "By Item" : "Line detail"}
            </button>
          ))}
        </div>
        )}

        <div style={{ background: "#fff", border: "1px solid var(--omh-border)", borderTop: "none", borderRadius: "0 0 12px 12px", overflow: "auto" }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "var(--omh-text-muted)" }}>Loading…</div>}

          {!loading && (projected || view === "summary") && data && (
            <table data-testid="sales-report-summary-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={TH}>Customer</th>
                  {activeCcys.map((c) => <th key={c} style={{ ...TH, textAlign: "right" }}>{c}</th>)}
                  <th style={{ ...TH, textAlign: "right" }}>{projected ? "Open Value" : "Net Value"}</th>
                  <th style={{ ...TH, textAlign: "right" }}>Qty</th>
                  <th style={{ ...TH, textAlign: "right" }}>{projected ? "Open Lines" : "Invoices"}</th>
                  <th style={TH}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={(r.ms_pcode || "") + r.customer_name} data-testid={`sales-report-row-${r.ms_pcode || r.customer_name}`}
                    onClick={() => { const nextF = { ...f, customer: r.ms_pcode || r.customer_name }; setF(nextF); load(undefined, nextF); }}
                    style={{ cursor: "pointer" }} onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={{ ...TD, fontWeight: 600 }}>{(r.customer_name || "").toUpperCase()}<span style={{ color: "var(--omh-text-muted)", fontWeight: 400, fontSize: 11.5, marginLeft: 6 }}>{r.ms_pcode}</span></td>
                    {activeCcys.map((c) => <td key={c} style={{ ...TD, textAlign: "right", ...MONO }}>{r.by_ccy[c] ? compact(r.by_ccy[c], c) : "—"}</td>)}
                    {(() => { const d = pickDisp(r.by_ccy, r.net_value_inr); return (
                      <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 700 }} title={d.mixed ? "Mixed currencies — shown as INR equivalent" : ""}>{compact(d.val, d.ccy)}{d.mixed ? INR_EQ : null}</td>
                    ); })()}
                    <td style={{ ...TD, textAlign: "right", ...MONO }}>{fmtNum(r.qty)}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO }}>{projected ? r.line_count : r.invoice_count}</td>
                    <td style={TD}><CaretRight size={14} color="var(--omh-text-muted)" /></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={activeCcys.length + 5} style={{ ...TD, textAlign: "center", color: "var(--omh-text-muted)", padding: 40 }}>{projected ? "No open orders match these filters." : "No sales match these filters."}</td></tr>}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                    <td style={{ ...TD, fontWeight: 800 }}>Total</td>
                    {activeCcys.map((c) => {
                      const tot = projected ? rows.reduce((s, r) => s + (r.by_ccy[c] || 0), 0) : (data.totals.by_ccy[c] || 0);
                      return <td key={c} style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }} title={tot ? compact(tot, c) : ""}>{tot ? fmtCur(tot, c) : "—"}</td>;
                    })}
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{compact(projected ? (data.totals.projected_open_orders_inr || 0) : data.totals.net_value_inr, "INR")}{INR_EQ}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{fmtNum(projected ? rows.reduce((s, r) => s + (r.qty || 0), 0) : data.totals.qty)}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{projected ? (data.totals.projected_open_orders_lines || 0) : data.totals.invoice_count}</td>
                    <td style={TD}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {!loading && !projected && view === "item" && data && (
            <table data-testid="sales-report-item-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={TH}>Item Code</th>
                  <th style={TH}>Description</th>
                  <th style={{ ...TH, textAlign: "right" }}>Shipped Qty</th>
                  <th style={{ ...TH, textAlign: "right" }}>Shipped Value</th>
                  <th style={{ ...TH, textAlign: "right" }}>To-Ship Qty</th>
                  <th style={{ ...TH, textAlign: "right" }}>To-Ship Value</th>
                  <th style={{ ...TH, textAlign: "right" }}>Order Book</th>
                </tr>
              </thead>
              <tbody>
                {(data.item_summary || []).map((r, i) => (
                  <tr key={r.item_code + i} data-testid={`sales-report-item-row-${r.item_code}`}
                    onClick={() => { const nextF = { ...f, item_number: r.item_code }; setF(nextF); setView("detail"); load("detail", nextF); }}
                    style={{ cursor: "pointer" }} onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={{ ...TD, ...MONO, fontWeight: 600 }}>{r.item_code}</td>
                    <td style={{ ...TD, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.item_desc}>{r.item_desc || "—"}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO }}>{fmtNum(r.shipped_qty)}</td>
                    {(() => { const d = pickDisp(r.shipped_by_ccy, r.shipped_value_inr); return (
                      <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 700 }} title={d.mixed ? "Mixed currencies — INR equivalent" : ""}>{d.val ? <>{compact(d.val, d.ccy)}{d.mixed ? INR_EQ : null}</> : "—"}</td>
                    ); })()}
                    <td style={{ ...TD, textAlign: "right", ...MONO, color: r.to_ship_qty ? "#059669" : "var(--omh-text-muted)" }}>{r.to_ship_qty ? fmtNum(r.to_ship_qty) : "—"}</td>
                    {(() => { const d = pickDisp(r.to_ship_by_ccy, r.to_ship_value_inr); return (
                      <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 700, color: d.val ? "#047857" : "var(--omh-text-muted)" }} title={d.mixed ? "Mixed currencies — INR equivalent" : ""}>{d.val ? <>{compact(d.val, d.ccy)}{d.mixed ? INR_EQ : null}</> : "—"}</td>
                    ); })()}
                    {(() => { const d = pickDisp(mergeCcy(r.shipped_by_ccy, r.to_ship_by_ccy), r.order_book_value_inr); return (
                      <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }} title={d.mixed ? "Mixed currencies — INR equivalent" : ""}>{compact(d.val, d.ccy)}{d.mixed ? INR_EQ : null}</td>
                    ); })()}
                  </tr>
                ))}
                {(data.item_summary || []).length === 0 && <tr><td colSpan={7} style={{ ...TD, textAlign: "center", color: "var(--omh-text-muted)", padding: 40 }}>No items match these filters.</td></tr>}
              </tbody>
              {(data.item_summary || []).length > 0 && (
                <tfoot>
                  <tr style={{ background: "#f8fafc", fontWeight: 800 }}>
                    <td style={{ ...TD, fontWeight: 800 }} colSpan={2}>Total · {fmtNum(data.item_summary.length)} items</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{fmtNum(data.item_summary.reduce((s, r) => s + (r.shipped_qty || 0), 0))}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{compact(data.item_summary.reduce((s, r) => s + (r.shipped_value_inr || 0), 0), "INR")}{INR_EQ}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{fmtNum(data.item_summary.reduce((s, r) => s + (r.to_ship_qty || 0), 0))}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{compact(data.item_summary.reduce((s, r) => s + (r.to_ship_value_inr || 0), 0), "INR")}{INR_EQ}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 800 }}>{compact(data.item_summary.reduce((s, r) => s + (r.order_book_value_inr || 0), 0), "INR")}{INR_EQ}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {!loading && !projected && view === "detail" && data && (
            <table data-testid="sales-report-detail-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
              <thead>
                <tr>
                  <th style={TH}>Date</th>
                  <th style={TH}>GST Inv Num</th>
                  <th style={TH}>Export Inv Num</th>
                  <th style={TH}>Customer</th>
                  <th style={TH}>Item Code</th>
                  <th style={TH}>Description</th>
                  <th style={TH}>PO</th>
                  <th style={{ ...TH, textAlign: "right" }}>Qty</th>
                  <th style={{ ...TH, textAlign: "right" }}>Rate</th>
                  <th style={{ ...TH, textAlign: "right" }}>Net Value</th>
                </tr>
              </thead>
              <tbody>
                {data.detail.map((r, i) => (
                  <tr key={i} data-testid={`sales-report-detail-row-${i}`}>
                    <td style={{ ...TD, ...MONO, whiteSpace: "nowrap" }}>{fmtDate(r.sale_date)}</td>
                    <td style={{ ...TD, ...MONO }}>{r.invoice_no || "\u2014"}</td>
                    <td style={{ ...TD, ...MONO }}>{r.export_invoice_no || "\u2014"}</td>
                    <td style={TD}>{r.customer_name}</td>
                    <td style={{ ...TD, ...MONO, fontWeight: 600 }}>{r.item_code}</td>
                    <td style={{ ...TD, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.item_desc}>{r.item_desc}</td>
                    <td style={{ ...TD, ...MONO }}>{r.pono}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO }}>{fmtNum(r.qty)}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO }}>{fmtCur(r.rate, r.currency)}</td>
                    <td style={{ ...TD, textAlign: "right", ...MONO, fontWeight: 700 }}>{fmtCur(r.net_value, r.currency)}</td>
                  </tr>
                ))}
                {data.detail.length === 0 && <tr><td colSpan={9} style={{ ...TD, textAlign: "center", color: "var(--omh-text-muted)", padding: 40 }}>No line items match these filters.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        {view === "detail" && data?.detail?.length > 0 && (
          <div style={{ fontSize: 12, color: "var(--omh-text-muted)", marginTop: 8 }}>{fmtNum(data.detail.length)} line items</div>
        )}
      </div>
    </Layout>
  );
}
